import {
  ModalProps,
  createUseModal,
  useModal,
} from "@imminently/react/context/modals";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@imminently/react/ui/core/dialog";
import { Text } from "@imminently/react/ui/core/text";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { Icon } from "@/components/Icon";
import { RoleValues } from "@/resources/types";
import { useCreateUser, useUser, useUpdateUser } from "@/resources/users";

type FieldProps<T> = { value: T; onChange: Dispatch<SetStateAction<T>> };

const changeHandler = <T,>(onChange: (newValue: T) => void) => {
  return (event: any) => onChange(event.target.value);
};

const styles = {
  content: {
    "@media (min-width: 650px)": {
      minWidth: "36rem",
      maxWidth: "36rem",
    },
  },
};

const RoleField = ({ value, onChange }: FieldProps<string>) => (
  <TextField
    sx={{ "& .MuiSelect-select": { py: 1, px: 2 } }}
    select
    label="Status"
    value={value}
    onChange={changeHandler(onChange)}
  >
    {RoleValues.map((role) => (
      <MenuItem key={role} value={role}>
        {role}
      </MenuItem>
    ))}
  </TextField>
);
// TODO this file should have the following changes
// - Update to use react-hook-form
// - Combine create and edit into a single modal (use useMutateResource hook)
// - Use the form components once they have been back ported

// Create Location

const CreateModal = () => {
  const { popModal: close } = useModal();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("user");
  // const [status, setStatus] = useState<Status>(StatusValues[0]);
  const [createUser] = useCreateUser({
    mutationKey: "create-user",
    invalidateQueries: ["data-list-users"],
  });

  const handleSubmit = async () => {
    await createUser({ payload: { firstName, lastName, email, role } });
    close();
  };

  return (
    <Dialog open onClose={close} maxWidth="sm">
      <DialogTitle>
        <Text variant="h3">Add User</Text>
        <IconButton tabIndex={-1} onClick={close}>
          <Icon icon="close" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.content}>
        <Stack mt={3} gap={2}>
          <TextField
            label="First Name"
            variant="outlined"
            value={firstName}
            onChange={changeHandler(setFirstName)}
          />
          <TextField
            label="Last Name"
            variant="outlined"
            value={lastName}
            onChange={changeHandler(setLastName)}
          />
          <TextField
            label="Email"
            variant="outlined"
            value={email}
            onChange={changeHandler(setEmail)}
          />
          <RoleField value={role} onChange={setRole} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Box flex={1} />
        <Button
          disabled={firstName === "" || lastName === "" || email === "" || role === ""}
          onClick={handleSubmit}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const useCreateUserModal = createUseModal(CreateModal);

// Edit Location

type EditModalProps = { id: string };

const EditModal = ({ props }: ModalProps<EditModalProps>) => {
  const { popModal: close } = useModal();
  const { id } = props;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");

  const { data: user } = useUser(id);

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setEmail(user.email);
      setRole(user.role);
    }
  }, [user]);

  const [updateUser] = useUpdateUser({
    mutationKey: `edit-user-${id}`,
    invalidateQueries: ["users"],
  });

  const handleSubmit = async () => {
    await updateUser({ id, payload: { firstName, lastName, email, role } });
    close();
  };

  return (
    <Dialog open onClose={close} maxWidth="sm">
      <DialogTitle>
        <Text variant="h3">Edit user</Text>
        <IconButton tabIndex={-1} onClick={close}>
          <Icon icon="close" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.content}>
        <Stack mt={3} gap={2}>
          <TextField
            label="First Name"
            variant="outlined"
            value={firstName}
            onChange={changeHandler(setFirstName)}
          />
          <TextField
            label="Last Name"
            variant="outlined"
            value={lastName}
            onChange={changeHandler(setLastName)}
          />
          <TextField
            label="Email"
            variant="outlined"
            value={email}
            onChange={changeHandler(setEmail)}
          />
          <RoleField value={role} onChange={setRole} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Box flex={1} />
        <Button
          disabled={firstName === "" || lastName === "" || email === "" || role === ""}
          onClick={handleSubmit}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const useEditUserModal = createUseModal(EditModal);
